import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Flame, BadgePercent } from "lucide-react";
import Layout from "../components/Layout";
import Breadcrumbs from "../components/Breadcrumbs";
import QuoteForm from "../components/QuoteForm";
import PreFooterCTA from "../components/PreFooterCTA";
import { BUSINESS, SERVICE_AREAS } from "../data/business";

const BREADCRUMBS = [{ label: "10% Off" }];

// =============================================================================
// DiscountHero — copy of the Contact hero, re-messaged for the 10% OFF promo.
// Same 2-col layout (intro + CTAs left, GHL form right); mobile collapses
// to a compact title + form-first, like the contact page.
// =============================================================================
function DiscountHero() {
  return (
    <section className="relative isolate overflow-hidden text-white lg:min-h-[calc(100vh-100px)] lg:flex lg:items-center">
      <div className="absolute inset-0 -z-10 bg-[#1A1D21]" />
      <div className="absolute inset-0 -z-10 abc-grid-pattern opacity-30" />

      <div className="abc-container w-full py-8 lg:py-12 grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <Breadcrumbs items={BREADCRUMBS} />

          {/* Limited-time offer badge */}
          <div
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#E31D2B]/15 border border-[#E31D2B]/40 text-[#E31D2B] px-3.5 py-1.5 text-[13px] font-bold"
            data-testid="discount-badge"
          >
            <BadgePercent className="w-4 h-4" /> Limited time: 10% OFF your service
          </div>

          {/* Mobile-only compact title */}
          <h1
            className="font-display mt-3 lg:hidden text-[28px] leading-[1.1] font-extrabold"
            data-testid="discount-h1-mobile"
          >
            Save <span className="text-[#E31D2B]">10% OFF</span> Your Service
          </h1>
          <p className="mt-2 lg:hidden text-[14px] text-white/85" data-testid="discount-subhead-mobile">
            Book through this page and take <span className="font-semibold text-white">10% off</span> your drain, sewer, or waterproofing service. No obligation.
          </p>

          {/* Desktop-only full intro + CTAs */}
          <h1
            className="font-display mt-3 hidden lg:block text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight"
            data-testid="discount-h1"
          >
            Save <span className="text-[#E31D2B]">10% OFF</span> Your Service
          </h1>
          <p className="mt-4 hidden lg:block text-white/85 max-w-xl text-base md:text-lg">
            For a limited time, take <span className="font-semibold text-white">10% off</span> your next drain, sewer, or waterproofing service. Request your free, no-obligation quote below — or call and a real person answers 24/7.
          </p>

          <div className="mt-5 hidden lg:flex flex-wrap gap-3">
            <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent text-base py-3.5 px-5" data-testid="discount-hero-call">
              <Phone className="w-5 h-5" /> Call {BUSINESS.phone}
            </a>
            <a href={BUSINESS.emailHref} className="abc-btn abc-btn-ghost-light text-base py-3.5 px-5" data-testid="discount-hero-email">
              <Mail className="w-5 h-5" /> Email us
            </a>
          </div>

          <div className="mt-5 hidden lg:flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#E31D2B]" /> {BUSINESS.hours.weekdays}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#54616E]" /> {BUSINESS.address}</span>
          </div>
        </div>

        <div className="lg:col-span-5 mt-3 lg:mt-0">
          <QuoteForm source="discount-page" />
        </div>
      </div>
    </section>
  );
}

function ContactDetails() {
  return (
    <div className="abc-card p-6">
      <h3 className="font-display font-bold text-lg text-[#1A1D21]">Contact details</h3>
      <div className="mt-4 space-y-3 text-sm">
        <a href={BUSINESS.phoneHref} className="flex items-center gap-3 text-[#1A1D21] hover:text-[#54616E]" data-testid="discount-phone">
          <Phone className="w-5 h-5 text-[#E31D2B]" /> {BUSINESS.phone}
        </a>
        <a href={BUSINESS.emailHref} className="flex items-center gap-3 text-[#1A1D21] hover:text-[#54616E] break-all" data-testid="discount-email">
          <Mail className="w-5 h-5 text-[#E31D2B] flex-shrink-0" /> {BUSINESS.email}
        </a>
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-[#E31D2B] mt-0.5 flex-shrink-0" /> <span>{BUSINESS.address}</span>
        </div>
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-[#E31D2B] mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-semibold text-[#1A1D21]">{BUSINESS.hours.display}</span>
            <br />
            <span>{BUSINESS.hours.detail}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

// Free-estimate offer highlight.
function OfferCard() {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#1A1D21] to-[#262A30] text-white p-6 flex gap-4 items-start" data-testid="discount-featured">
      <BadgePercent className="w-8 h-8 text-[#E31D2B] flex-shrink-0" />
      <div>
        <h3 className="font-display font-bold text-lg">10% OFF your service</h3>
        <p className="text-white/75 text-sm mt-1">Book through this page and we&apos;ll take 10% off your drain, sewer, or waterproofing job. No obligation — just an honest, upfront price.</p>
        <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent mt-4" data-testid="discount-featured-call">
          <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
        </a>
      </div>
    </div>
  );
}

function ServiceAreaCard() {
  return (
    <div className="abc-card p-6">
      <h3 className="font-display font-bold text-lg text-[#1A1D21] flex items-center gap-2">
        <MapPin className="w-4 h-4 text-[#54616E]" /> Service area
      </h3>
      <ul className="mt-3 grid grid-cols-2 gap-1.5 text-sm">
        {SERVICE_AREAS.map((a) => (
          <li key={a.slug}>
            <Link to={`/service-areas/${a.slug}`} className="text-slate-700 hover:text-[#54616E]">{a.city}, {a.state}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MobileOtherWays() {
  return (
    <section className="py-8 lg:hidden bg-[#1A1D21] text-white" data-testid="discount-other-ways-mobile">
      <div className="abc-container">
        <h2 className="font-display text-xl font-extrabold">Prefer to talk?</h2>
        <p className="mt-1 text-white/75 text-sm">
          A real person picks up 24/7 — day or night, every day of the week.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-2.5">
          <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent w-full justify-center py-3.5" data-testid="discount-mobile-call">
            <Phone className="w-5 h-5" /> Call {BUSINESS.phone}
          </a>
          <a href={BUSINESS.emailHref} className="abc-btn abc-btn-ghost-light w-full justify-center py-3.5" data-testid="discount-mobile-email">
            <Mail className="w-5 h-5" /> Email us
          </a>
        </div>
        <div className="mt-4 space-y-2 text-sm text-white/85">
          <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#E31D2B]" /> {BUSINESS.hours.weekdays}</div>
          <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#54616E] mt-0.5 flex-shrink-0" /> <span>{BUSINESS.address}</span></div>
        </div>
      </div>
    </section>
  );
}

function GbpEmbedSlot() {
  return (
    <div
      className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50 relative h-full min-h-[460px]"
      data-testid="discount-gbp-map"
      aria-label="Google Maps location for Drain Sights"
    >
      <iframe
        title="Drain Sights location on Google Maps"
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3090.6323122498343!2d-76.6189962!3d39.22851569999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c802c0233ec133%3A0x4937d286f6772ecd!2sDrain%20Sights!5e0!3m2!1sen!2sus!4v1782249138596!5m2!1sen!2sus"
        className="absolute inset-0 w-full h-full"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

export default function Discount() {
  return (
    <Layout>
      <DiscountHero />
      <MobileOtherWays />

      <section className="py-10 md:py-14 abc-bg-pale">
        <div className="abc-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-6">
            <ContactDetails />
            <OfferCard />
            <ServiceAreaCard />
          </div>

          <div className="lg:col-span-7">
            <GbpEmbedSlot />
          </div>
        </div>
      </section>

      <PreFooterCTA title="Claim your 10% off" subtitle="Book through this page to save 10% — a real person picks up 24/7." />
    </Layout>
  );
}
